/**
 * PGD 1a: Nitrofurantoin for uncomplicated lower UTI
 * Version: 1.2
 * Effective: 2024-01-31
 * Expiry: 2027-01-30
 *
 * QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
 */

import type { PGD } from "../types";

export const pgdNitrofurantoinUti: PGD = {
  id: "pgd-1a-nitrofurantoin-uti",
  reference: "1a",
  version: "1.2",
  drugRef: "nitrofurantoin",
  indication: "Uncomplicated lower urinary tract infection",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",

  population: {
    ageRange: { min: 16, max: 64 },
    sex: "female_only",
    pregnancyAllowed: false,
    breastfeedingAllowed: false,
  },

  duration: { days: 3 },

  doseBands: [
    {
      id: "nitro-mr",
      resolveBy: "single",
      dose: 100,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "nitro-100-mr-cap",
          instruction: "Take ONE 100mg capsule TWICE a day",
          quantity: {
            count: 6,
            label: "6 capsules",
          },
        },
      ],
    },
    {
      id: "nitro-ir",
      resolveBy: "single",
      dose: 50,
      frequency: "FOUR times a day",
      formulations: [
        {
          formulationId: "nitro-50-ir-tab",
          instruction: "Take ONE 50mg tablet FOUR times a day",
          quantity: {
            count: 12,
            label: "12 tablets",
          },
        },
        {
          formulationId: "nitro-50-ir-cap",
          instruction: "Take ONE 50mg capsule FOUR times a day",
          quantity: {
            count: 12,
            label: "12 capsules",
          },
        },
      ],
    },
  ],

  preSupplyChecks: [
    { id: "ex-allergy", question: "Is the patient allergic to nitrofurantoin?" },
    { id: "ex-breastfeeding", question: "Is the patient currently breastfeeding?" },
    { id: "ex-recent-nitro", question: "Nitrofurantoin used in the past 3 months?" },
    { id: "ex-recent-abx", question: "Any antibiotic for UTI in the past 3 months?" },
    { id: "ex-failed-abx", question: "Failed previous antibiotic for this episode?" },
    { id: "ex-resistant", question: "Known nitrofurantoin-resistant UTI?" },
    { id: "ex-prophylactic", question: "On prophylactic antibiotics for UTI?" },
    { id: "ex-typhoid", question: "Oral typhoid vaccine within ±3 days?" },
    { id: "ex-catheter", question: "Currently using urinary catheter?" },
    {
      id: "ex-recurrent",
      question: "Recurrent UTI? (2+ in 6 months or 3+ in 12 months)",
    },
    { id: "ex-complicated", question: "Complicated UTI under urologist care?" },
    { id: "ex-ckd", question: "CKD stage 3b, 4 or 5 (eGFR below 45)?" },
    { id: "ex-diabetes", question: "Diabetes (Type 1 or 2)?" },
    {
      id: "ex-g6pd",
      question: "G6PD deficiency, porphyria, anaemia, or folate/B12 deficiency?",
    },
    { id: "ex-neuro-pulm", question: "Peripheral neuropathy or pulmonary disease?" },
    { id: "ex-hepatic", question: "Hepatic dysfunction?" },
    {
      id: "ex-hosp",
      question:
        "Hospitalised abroad <3m, care home, or UK hospital >7 days in 6m?",
    },
  ],

  cautions: [
    {
      id: "caution-duration",
      text: "Complete the full 3-day course even if symptoms resolve",
    },
    {
      id: "caution-urine-color",
      text: "Urine may turn yellow or brown — this is normal",
    },
    {
      id: "caution-otc-products",
      text: "Avoid OTC potassium/sodium citrate products",
    },
    {
      id: "caution-antacids",
      text: "Avoid antacids containing magnesium trisilicate",
    },
  ],

  counselling: [
    "Complete the full 3-day course",
    "Urine may turn yellow or brown — this is normal",
    "Return if no improvement within 48 hours",
    "Avoid OTC potassium/sodium citrate products",
    "Avoid antacids containing magnesium trisilicate",
    "Seek urgent help if symptoms worsen",
    "999 or A&E if signs of sepsis",
  ],
};
